import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  getDeviceSession,
  saveDeviceSession,
  clearDeviceSession,
  KioskDeviceSession,
} from '@/lib/kiosk/device-session';

interface UseKioskDeviceSessionReturn {
  // Session state
  session: KioskDeviceSession | null;
  isPaired: boolean;
  isValidating: boolean;
  deviceStatus: 'active' | 'inactive' | 'revoked' | 'unknown';

  // Business info
  businessId: string | null;
  businessName: string | null;
  deviceName: string | null;
  allowedModes: string[];

  // Pairing state
  pairingCode: string | null;
  pairingExpiresAt: Date | null;
  pairingError: string | null;
  isPairingCodeLoading: boolean;
  deviceId: string | null;

  // Actions
  requestPairingCode: () => Promise<void>;
  unpair: () => void;
  refreshValidation: () => Promise<void>;
}

/**
 * Hook for managing kiosk device pairing and session
 * Replaces credential-based authentication with permanent device pairing
 */
export function useKioskDeviceSession(): UseKioskDeviceSessionReturn {
  const [session, setSession] = useState<KioskDeviceSession | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [deviceStatus, setDeviceStatus] = useState<'active' | 'inactive' | 'revoked' | 'unknown'>('unknown');

  // Pairing state
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingExpiresAt, setPairingExpiresAt] = useState<Date | null>(null);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [isPairingCodeLoading, setIsPairingCodeLoading] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);

  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  /**
   * Validate existing session with the server
   */
  const validateSession = useCallback(async (storedSession: KioskDeviceSession): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('kiosk-validate-device', {
        body: {
          device_id: storedSession.device_id,
          session_token: storedSession.session_token,
        },
      });

      if (error) {
        console.error('Session validation error:', error);
        return false;
      }

      if (data?.valid) {
        setDeviceStatus(data.device?.is_active ? 'active' : 'inactive');

        // Update session with fresh data from server
        if (data.device) {
          const updatedSession: KioskDeviceSession = {
            ...storedSession,
            business_name: data.device.business?.name || storedSession.business_name,
            device_name: data.device.name || storedSession.device_name,
            allowed_modes: data.device.allowed_modes || storedSession.allowed_modes,
          };
          saveDeviceSession(updatedSession);
          setSession(updatedSession);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to validate session:', error);
      return false;
    }
  }, []);

  /**
   * Send heartbeat to server
   */
  const sendHeartbeat = useCallback(async () => {
    if (!session) return;

    try {
      const { data } = await supabase.functions.invoke('kiosk-device-heartbeat', {
        body: {
          device_id: session.device_id,
          session_token: session.session_token,
        },
      });

      if (data?.device_status === 'revoked') {
        // Device was revoked, clear session
        clearDeviceSession();
        setSession(null);
        setDeviceStatus('revoked');
      } else if (data?.device_status) {
        setDeviceStatus(data.device_status);
      }
    } catch (error) {
      console.error('Heartbeat error:', error);
    }
  }, [session]);

  /**
   * Request a new pairing code
   */
  const requestPairingCode = useCallback(async () => {
    setIsPairingCodeLoading(true);
    setPairingError(null);

    try {
      const { data, error } = await supabase.functions.invoke('kiosk-generate-pairing-code', {
        body: {
          device_info: {
            user_agent: navigator.userAgent,
            device_type: detectDeviceType(),
          },
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to generate pairing code');
      }

      setPairingCode(data.pairing_code);
      setPairingExpiresAt(new Date(data.expires_at));
      setDeviceId(data.device_id);

      // Subscribe to realtime channel for pairing completion
      setupPairingListener(data.device_id);
    } catch (error: any) {
      console.error('Failed to request pairing code:', error);
      setPairingError(error.message || 'Failed to generate pairing code');
    } finally {
      setIsPairingCodeLoading(false);
    }
  }, []);

  /**
   * Setup realtime listener for pairing completion
   */
  const setupPairingListener = useCallback((deviceId: string) => {
    // Clean up existing channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    const channel = supabase
      .channel(`kiosk:pairing:${deviceId}`)
      .on('broadcast', { event: 'paired' }, (payload) => {
        const { session_token, business_id, device_name, allowed_modes } = payload.payload;

        // Create and save the new session
        const newSession: KioskDeviceSession = {
          device_id: deviceId,
          session_token,
          business_id,
          business_name: '', // Will be populated on validation
          device_name: device_name || 'Kiosk',
          allowed_modes: allowed_modes || ['staff', 'customer'],
          paired_at: Date.now(),
        };

        saveDeviceSession(newSession);
        setSession(newSession);
        setDeviceStatus('active');
        setPairingCode(null);
        setPairingExpiresAt(null);
        setDeviceId(null);

        // Validate to get business name
        validateSession(newSession);

        // Clean up channel
        supabase.removeChannel(channel);
        realtimeChannelRef.current = null;
      })
      .subscribe();

    realtimeChannelRef.current = channel;
  }, [validateSession]);

  /**
   * Unpair the device
   */
  const unpair = useCallback(() => {
    // Clean up heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Clean up realtime channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    clearDeviceSession();
    setSession(null);
    setDeviceStatus('unknown');
    setPairingCode(null);
    setPairingExpiresAt(null);
  }, []);

  /**
   * Refresh validation
   */
  const refreshValidation = useCallback(async () => {
    const storedSession = getDeviceSession();
    if (storedSession) {
      setIsValidating(true);
      await validateSession(storedSession);
      setIsValidating(false);
    }
  }, [validateSession]);

  // Initialize: check for existing session
  useEffect(() => {
    const initSession = async () => {
      setIsValidating(true);
      const storedSession = getDeviceSession();

      if (storedSession) {
        setSession(storedSession);
        const isValid = await validateSession(storedSession);

        if (!isValid) {
          clearDeviceSession();
          setSession(null);
        }
      }

      setIsValidating(false);
    };

    initSession();
  }, [validateSession]);

  // Setup heartbeat interval when session is active
  useEffect(() => {
    if (session && deviceStatus === 'active') {
      // Send initial heartbeat
      sendHeartbeat();

      // Setup interval (every 30 seconds)
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000);

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      };
    }
  }, [session, deviceStatus, sendHeartbeat]);

  // Listen for storage changes (multi-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'taginn_kiosk_device_session') {
        const newSession = getDeviceSession();
        setSession(newSession);
        if (newSession) {
          validateSession(newSession);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [validateSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
    };
  }, []);

  return {
    session,
    isPaired: !!session,
    isValidating,
    deviceStatus,

    businessId: session?.business_id || null,
    businessName: session?.business_name || null,
    deviceName: session?.device_name || null,
    allowedModes: session?.allowed_modes || [],

    pairingCode,
    pairingExpiresAt,
    pairingError,
    isPairingCodeLoading,
    deviceId,

    requestPairingCode,
    unpair,
    refreshValidation,
  };
}

/**
 * Detect device type based on user agent
 */
function detectDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();

  if (/tablet|ipad|playbook|silk/.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua)) {
    return 'phone';
  }
  return 'desktop';
}
